import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Zap, CheckCircle2, Clock, MessageCircle } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { formatItalianDateTime } from "../../lib/dateUtils";
import { Card, Button, Spinner } from "../../components/ui-kit";

// Report serale del consulente (link dall'email/push delle 21:00): stato dei
// registri di ogni cliente e invio dell'avviso ⚡ ai clienti selezionati.
// Vedi backend/compliance_alerts.py.
const MAX_DAYS_BACK = 7;
const STATUS = {
  missing: { icon: "🔴", label: (r) => `Nessuna registrazione (0 su ${r.due})`, cls: "text-red-700" },
  partial: { icon: "🟠", label: (r) => `Parziale (${r.done} su ${r.due})`, cls: "text-amber-700" },
  complete: { icon: "🟢", label: (r) => `Completo (${r.due} su ${r.due})`, cls: "text-emerald-700" },
  none_due: { icon: "⚪", label: () => "Nessun registro previsto", cls: "text-slate-500" },
};

function shiftDay(dateKey, delta) {
  const [y, m, d] = dateKey.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + delta));
  return dt.toISOString().slice(0, 10);
}
const romeToday = () => new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Rome" }).format(new Date());
function dayLabel(dateKey) {
  const [y, m, d] = dateKey.split("-");
  return `${d}/${m}/${y}`;
}

function MissingList({ names }) {
  const [open, setOpen] = useState(false);
  if (!names.length) return null;
  const shown = open ? names : names.slice(0, 3);
  return <p className="mt-1 break-words text-xs text-slate-600">
    Mancano: {shown.join(", ")}
    {names.length > 3 && <button type="button" onClick={() => setOpen(!open)} className="ml-1 font-semibold text-blue-700 hover:underline">{open ? "mostra meno" : `e altre ${names.length - 3}`}</button>}
  </p>;
}

function AlertState({ alert }) {
  if (!alert) return null;
  return <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
    <span className="inline-flex items-center gap-1 font-semibold text-navy"><Zap className="h-3.5 w-3.5 text-gold" /> Avvisato {formatItalianDateTime(alert.created_at)}</span>
    {alert.acknowledged_at
      ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Presa visione {formatItalianDateTime(alert.acknowledged_at)}{alert.acknowledged_by_name ? ` (${alert.acknowledged_by_name})` : ""}</span>
      : <span className="inline-flex items-center gap-1 text-amber-700"><Clock className="h-3.5 w-3.5" /> Presa visione: non ancora</span>}
  </div>;
}

// WhatsApp (wa.me): apre la chat del cliente con il testo già scritto, l'invio
// lo fa il consulente dal proprio WhatsApp. Non passa dal server e non viene
// registrato nello storico avvisi.
function whatsappLink(row, message) {
  const text = `⚡ *Avviso del tuo consulente HACCP*\n\n${row.intro}\n\n${row.alert_message || message}\n\nDF Consulting Group`;
  return `https://wa.me/${row.whatsapp.number}?text=${encodeURIComponent(text)}`;
}

function WhatsAppAction({ row, message }) {
  if (!row.whatsapp) {
    return <p className="text-xs font-medium text-red-700">Numero WhatsApp mancante: <Link to={`/admin/tenants/${row.tenant_id}`} className="underline">aggiungilo nella scheda azienda</Link></p>;
  }
  return <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
    <a href={whatsappLink(row, message)} target="_blank" rel="noreferrer"
      className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg bg-[#25D366] px-3 text-sm font-semibold text-white hover:brightness-95"
      data-testid={`report-serale-whatsapp-${row.tenant_id}`}>
      <MessageCircle className="h-4 w-4" /> WhatsApp
    </a>
    <span className="text-xs text-slate-500">+{row.whatsapp.number}{row.whatsapp.owner ? ` · ${row.whatsapp.owner}` : ""}{row.whatsapp.source === "phone" ? " · telefono principale, nessun numero WhatsApp inserito" : ""}</span>
  </div>;
}

export default function ReportSerale() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const dateKey = params.get("data") || "";

  const load = useCallback(() => {
    setError("");
    api.get("/admin/compliance-alerts/overview", { params: dateKey ? { date: dateKey } : {} })
      .then(({ data: d }) => {
        setData(d);
        setMessage((current) => current || d.default_message);
        // Preselezionati: chi ha registri mancanti/parziali e non è ancora stato avvisato.
        setSelected(new Set(d.items.filter((r) => ["missing", "partial"].includes(r.status) && !r.alert).map((r) => r.tenant_id)));
      })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Report non disponibile"));
  }, [dateKey]);
  useEffect(() => { setData(null); load(); }, [load]);

  const alertable = useMemo(() => (data?.items || []).filter((r) => ["missing", "partial"].includes(r.status)), [data]);
  const selectable = alertable.filter((r) => !r.alert);
  const others = (data?.items || []).filter((r) => !["missing", "partial"].includes(r.status));
  const counts = useMemo(() => (data?.items || []).reduce((acc, r) => ({ ...acc, [r.status]: (acc[r.status] || 0) + 1 }), {}), [data]);

  const toggle = (id) => setSelected((cur) => { const next = new Set(cur); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const allSelected = selectable.length > 0 && selectable.every((r) => selected.has(r.tenant_id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectable.map((r) => r.tenant_id)));

  const goTo = (key) => setParams(key ? { data: key } : {});
  const currentKey = data?.date_key;
  const oldestKey = shiftDay(romeToday(), -MAX_DAYS_BACK);

  const send = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    const names = alertable.filter((r) => selected.has(r.tenant_id)).map((r) => r.name);
    if (!window.confirm(`Inviare l’avviso ⚡ a ${ids.length} ${ids.length === 1 ? "cliente" : "clienti"}?\n\n${names.join("\n")}\n\nIl titolare lo riceverà nell’app, come notifica push e via email.`)) return;
    setSending(true);
    try {
      const { data: res } = await api.post("/admin/compliance-alerts/send", { tenant_ids: ids, message, date_key: currentKey });
      const sent = res.items.filter((i) => i.result === "sent");
      const noOwner = sent.filter((i) => !i.recipients).length;
      const notNeeded = res.items.filter((i) => i.result === "not_needed").length;
      const already = res.items.filter((i) => i.result === "already_sent").length;
      toast.success(`⚡ Avviso inviato a ${sent.length} ${sent.length === 1 ? "cliente" : "clienti"}`);
      if (notNeeded) toast.info(`${notNeeded} ${notNeeded === 1 ? "cliente ha" : "clienti hanno"} completato i registri nel frattempo: non avvisati`);
      if (already) toast.info(`${already} già avvisati oggi: non reinviato`);
      if (noOwner) toast.warning(`${noOwner} senza titolare attivo: vedranno solo il banner nell’app`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invio non riuscito");
    } finally { setSending(false); }
  };

  return <div className="mx-auto max-w-4xl space-y-5" data-testid="report-serale">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="font-heading text-2xl font-bold text-navy">Report serale</h1>
        <p className="text-sm text-slate-500">Registri HACCP dei clienti attivi e avvisi ⚡ a chi non li ha completati.</p>
      </div>
      {currentKey && <div className="flex items-center gap-1" data-testid="report-serale-date-nav">
        <button type="button" aria-label="Giorno precedente" disabled={currentKey <= oldestKey || sending}
          onClick={() => goTo(shiftDay(currentKey, -1))}
          className="grid h-11 w-11 place-items-center rounded-lg text-navy hover:bg-slate-100 disabled:opacity-40"><ChevronLeft className="h-5 w-5" /></button>
        <span className="min-w-[110px] text-center font-heading font-semibold text-navy">{data.is_today ? `Oggi ${dayLabel(currentKey).slice(0, 5)}` : dayLabel(currentKey)}</span>
        <button type="button" aria-label="Giorno successivo" disabled={data.is_today || sending}
          onClick={() => { const next = shiftDay(currentKey, 1); goTo(next >= romeToday() ? "" : next); }}
          className="grid h-11 w-11 place-items-center rounded-lg text-navy hover:bg-slate-100 disabled:opacity-40"><ChevronRight className="h-5 w-5" /></button>
      </div>}
    </div>

    {error && <Card className="space-y-3 p-5 text-sm">
      <p className="text-red-700">{error}</p>
      <p className="text-slate-500">Si possono consultare solo oggi e gli ultimi {MAX_DAYS_BACK} giorni.</p>
      <Button variant="outline" onClick={() => goTo("")}>Vai a oggi</Button>
    </Card>}
    {!error && !data && <Spinner />}

    {data && <>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {["missing", "partial", "complete", "none_due"].map((key) => <Card key={key} className="p-3">
          <div className="text-2xl font-heading font-bold text-navy">{STATUS[key].icon} {counts[key] || 0}</div>
          <div className="text-xs text-slate-500">{{ missing: "Nessuna registrazione", partial: "Parziali", complete: "Completi", none_due: "Nessun registro previsto" }[key]}</div>
        </Card>)}
      </div>

      <Card className="space-y-4 p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-heading text-lg font-semibold text-navy">Da avvisare</h2>
          {selectable.length > 0 && <Button variant="ghost" onClick={toggleAll} data-testid="report-serale-toggle-all">{allSelected ? "Deseleziona tutti" : "Seleziona tutti"}</Button>}
        </div>
        {data.is_today && <p className="rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-900">I clienti possono ancora compilare i registri di oggi fino a mezzanotte: prima di inviare controlla che lo stato sia aggiornato.</p>}
        {alertable.length === 0 && <p className="text-sm text-emerald-700">Nessun cliente con registri mancanti o incompleti in questa giornata.</p>}
        <ul className="divide-y divide-hairline">
          {alertable.map((r) => {
            const disabled = Boolean(r.alert);
            return <li key={r.tenant_id} className="py-3">
              <label className={`flex gap-3 ${disabled ? "cursor-default" : "cursor-pointer"}`}>
                <input type="checkbox" className="mt-1 h-5 w-5 shrink-0 accent-navy" disabled={disabled}
                  checked={!disabled && selected.has(r.tenant_id)} onChange={() => toggle(r.tenant_id)}
                  aria-label={`Avvisa ${r.name}`} data-testid={`report-serale-check-${r.tenant_id}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                    <Link to={`/admin/tenants/${r.tenant_id}`} className="font-semibold text-navy hover:underline" onClick={(e) => e.stopPropagation()}>{r.name}</Link>
                    {r.alerts_this_month > 0 && <span className="text-xs font-semibold text-amber-700">⚡ {r.alerts_this_month} {r.alerts_this_month === 1 ? "avviso" : "avvisi"} questo mese</span>}
                  </div>
                  <p className={`text-sm font-medium ${STATUS[r.status].cls}`}>{STATUS[r.status].icon} {STATUS[r.status].label(r)}</p>
                  <MissingList names={r.missing} />
                  <AlertState alert={r.alert} />
                </div>
              </label>
              <div className="mt-2 pl-8"><WhatsAppAction row={r} message={message} /></div>
            </li>;
          })}
        </ul>

        {selectable.length > 0 && <div className="space-y-3 border-t border-hairline pt-4">
          <label className="block">
            <span className="text-sm font-semibold text-navy">Testo dell’avviso</span>
            <span className="block text-xs text-slate-500">Prima di questo testo ogni cliente vede in automatico la sua situazione: data, registri fatti su previsti ed elenco dei mancanti.</span>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={1000} rows={3}
              className="mt-2 w-full rounded-lg border border-hairline p-3 text-sm text-navy focus:border-navy focus:outline-none" data-testid="report-serale-message" />
          </label>
          <Button variant="accent" className="w-full sm:w-auto" loading={sending} disabled={!selected.size} onClick={send} data-testid="report-serale-send">
            <Zap className="h-4 w-4" /> Invia avviso a {selected.size} {selected.size === 1 ? "cliente" : "clienti"}
          </Button>
        </div>}
      </Card>

      {others.length > 0 && <Card className="p-4 sm:p-5">
        <h2 className="mb-2 font-heading text-lg font-semibold text-navy">In regola o senza registri previsti</h2>
        <ul className="divide-y divide-hairline">
          {others.map((r) => <li key={r.tenant_id} className="flex flex-wrap items-baseline justify-between gap-x-3 py-2 text-sm">
            <Link to={`/admin/tenants/${r.tenant_id}`} className="font-medium text-navy hover:underline">{r.name}</Link>
            <span className={STATUS[r.status].cls}>{STATUS[r.status].icon} {STATUS[r.status].label(r)}</span>
          </li>)}
        </ul>
      </Card>}
    </>}
  </div>;
}
